import React from "react";
import { MenuItem, Select, Typography, useTheme } from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { PaginatorProps } from "./types";
import { PaginatorContainer, NavBtn } from "./style";

const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 30, 50, 100];

const PaginatorCst: React.FC<PaginatorProps> = ({
  page,
  pageSize,
  totalItems,
  onPageChange,
  disabled = false,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageSizeChange,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("common");

  const totalPages = Math.max(Math.ceil(totalItems / pageSize), 1);
  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  const canGoPrev = !disabled && page > 1;
  const canGoNext = !disabled && page < totalPages;

  return (
    <PaginatorContainer>
      {onPageSizeChange && (
        <>
          <Typography
            sx={{ fontSize: "13px", color: theme.palette.text.primary }}
          >
            {t("paginator-rows-per-page")}
          </Typography>
          <Select
            value={pageSize}
            size="small"
            disabled={disabled}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            sx={{
              fontSize: "13px",
              borderRadius: "8px",
              "& .MuiSelect-select": { padding: "4px 8px" },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: "8px",
                  border: `1px solid ${theme.palette.divider}`,
                },
              },
            }}
          >
            {pageSizeOptions.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: "13px" }}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </>
      )}
      <Typography
        sx={{
          fontSize: "13px",
          color: theme.palette.text.primary,
          mr: 1,
        }}
      >
        {t("paginator-range", { from, to, total: totalItems })}
      </Typography>
      <NavBtn
        disabled={!canGoPrev}
        onClick={() => onPageChange(1)}
        aria-label="first page"
      >
        <ChevronsLeft size={18} />
      </NavBtn>
      <NavBtn
        disabled={!canGoPrev}
        onClick={() => onPageChange(page - 1)}
        aria-label="previous page"
      >
        <ChevronLeft size={18} />
      </NavBtn>
      <NavBtn
        disabled={!canGoNext}
        onClick={() => onPageChange(page + 1)}
        aria-label="next page"
      >
        <ChevronRight size={18} />
      </NavBtn>
      <NavBtn
        disabled={!canGoNext}
        onClick={() => onPageChange(totalPages)}
        aria-label="last page"
      >
        <ChevronsRight size={18} />
      </NavBtn>
    </PaginatorContainer>
  );
};

export default PaginatorCst;
